import { BellRing, CalendarDays, Contact2, Keyboard, LayoutDashboard, LifeBuoy, LogOut, MessageSquare, Network, Presentation, Settings, ShieldCheck } from 'lucide-react';
import { useQuery } from '@apollo/client';
import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { FOLLOW_UPS_QUERY } from '../lib/queries.js';
import { formatCount } from '../lib/format.js';
import { ReminderTray } from './ReminderTray.web.jsx';
import { useAuth } from '../lib/auth.jsx';
import { leaveFor } from '../lib/navigation.js';
import { ENV_NAME, PLATFORM_HOME } from '../lib/runtimeEnv.js';
import { useSession } from '../lib/session.jsx';
import { useShortcut, useShortcutHelp } from '../lib/shortcuts.jsx';
import { S } from '../lib/strings.js';

const ROLE_LABELS = { ADMIN: S.auth.roleAdmin, USER: S.auth.roleUser };

const navClass = ({ isActive }) =>
  [
    'flex h-control items-center gap-2 rounded px-4 text-base transition-colors',
    isActive ? 'bg-primary text-primary-contrast' : 'text-text hover:bg-surface-hover',
  ].join(' ');

/** Desktop-first shell: fixed sidebar + a dense content area, no long scroll. */
export function AppShell() {
  const openHelp = useShortcutHelp();
  const { logout, displayName } = useAuth();
  const { me, isAdmin, canPlatform, canCrm } = useSession();

  // Platform-only staff (platform_admin, no MeshHub role) have nothing here:
  // every CRM field would refuse them. They are sent to their half of the app
  // instead of being shown a CRM made of error states. `replace`, so Back does
  // not bounce them here again.
  const platformOnly = !!me && canPlatform && !canCrm;
  React.useEffect(() => {
    if (platformOnly) leaveFor(PLATFORM_HOME);
  }, [platformOnly]);

  // The badge is the whole point of an inbox: a follow-up nobody looks at is a
  // follow-up that does not happen. `errorPolicy: ignore` because a count is
  // never worth taking the shell down for.
  const { data: followUps } = useQuery(FOLLOW_UPS_QUERY, {
    variables: { state: 'OPEN' },
    errorPolicy: 'ignore',
  });
  const openFollowUps = followUps?.followUpSuggestions?.length ?? 0;

  // The other half of the app, as far as this caller may go: the whole
  // platform panel for platform_admin, else the demo-request queue — which
  // lives there and is open to every CRM role.
  const otherHalf = canPlatform
    ? { href: PLATFORM_HOME, label: S.nav.platform, Icon: ShieldCheck }
    : canCrm
      ? { href: `${PLATFORM_HOME}demo-requests`, label: S.nav.demoRequests, Icon: Presentation }
      : null;

  // Registered centrally, like every other shortcut. Ctrl/⌘+Shift+Q rather than
  // a bare key: signing out mid-task by mistyping would be infuriating.
  useShortcut('mod+shift+Q', logout, {
    description: S.shortcuts.actions.signOut,
    group: S.shortcuts.groups.session,
    allowInInput: true,
  });

  return (
    <div className="flex min-h-screen">
      <aside className="flex w-sidebar shrink-0 flex-col border-e border-border bg-surface">
        <div className="flex h-toolbar items-center gap-2 border-b border-border px-5">
          <Network size={18} className="text-primary" aria-hidden="true" />
          <span className="text-lg font-bold">{S.app.name}</span>
        </div>

        <nav className="flex flex-1 flex-col gap-1 p-3">
          {/* The dashboard is the landing page as of Step 6, so it is `/`
              AND `/dashboard` — `end` keeps it from staying highlighted on
              every child route. */}
          <NavLink to="/" end className={navClass}>
            <LayoutDashboard size={16} aria-hidden="true" />
            {S.nav.dashboard}
          </NavLink>

          <NavLink to="/contacts" className={navClass}>
            <Contact2 size={16} aria-hidden="true" />
            {S.nav.contacts}
          </NavLink>

          <NavLink to="/calendar" className={navClass}>
            <CalendarDays size={16} aria-hidden="true" />
            {S.calendar.title}
          </NavLink>

          <NavLink to="/followups" className={navClass}>
            <BellRing size={16} aria-hidden="true" />
            {S.followUps.title}
            {openFollowUps > 0 && (
              <span className="ms-auto rounded-full bg-accent px-2 text-xs text-primary-contrast">
                {formatCount(openFollowUps)}
              </span>
            )}
          </NavLink>

          <NavLink to="/messaging" className={navClass}>
            <MessageSquare size={16} aria-hidden="true" />
            {S.messaging.title}
          </NavLink>

          <NavLink to="/issues" className={navClass}>
            <LifeBuoy size={16} aria-hidden="true" />
            {S.issues.title}
          </NavLink>

          {/*
            Shown to EVERY role now: /settings/integrations is self-service,
            because a Google connection is one person's own calendar. The
            ADMIN-only sections inside still refuse on their own — hiding a nav
            entry was never the control (CLAUDE.md §7).
          */}
          <NavLink to="/settings" className={navClass}>
            <Settings size={16} aria-hidden="true" />
            {S.nav.settings}
          </NavLink>

          {/*
            A plain <a>, not a NavLink: /platform is the OTHER bundle (plan D6),
            so this is a page load with a silent sign-in, not a route change.
          */}
          {otherHalf && (
            <a
              href={otherHalf.href}
              className={`${navClass({ isActive: false })} mt-2 border-t border-border pt-2`}
              data-testid="nav-platform"
            >
              <otherHalf.Icon size={16} aria-hidden="true" />
              {otherHalf.label}
            </a>
          )}
        </nav>

        <ReminderTray />

        <div className="border-t border-border">
          <div className="flex flex-col gap-1 px-5 py-3">
            <span className="truncate text-base font-medium" title={me?.displayName ?? displayName}>
              {me?.displayName || displayName || S.app.none}
            </span>
            <span className="text-xs text-text-muted">
              {ROLE_LABELS[me?.role] ?? me?.role ?? ''}
              {me?.bypassed ? ' · BYPASS_AUTH' : ''}
            </span>
            {/* Which deployment this is: beta1 and production serve the same
                image, and a CRM that looks identical in both is one where
                somebody eventually messages real customers from the wrong one.
                LTR: an environment name is an identifier. */}
            <span className="text-xs text-text-muted" dir="ltr" data-testid="shell-env">
              {ENV_NAME}
            </span>
          </div>

          <button
            type="button"
            onClick={openHelp}
            className="flex h-control w-full items-center gap-2 px-5 text-sm text-text-muted
                       hover:bg-surface-hover"
          >
            <Keyboard size={15} aria-hidden="true" />
            {S.nav.shortcuts}
            <kbd className="ms-auto rounded border border-border px-2 text-xs">؟</kbd>
          </button>

          <button
            type="button"
            onClick={logout}
            className="flex h-control w-full items-center gap-2 px-5 text-sm text-text-muted
                       hover:bg-surface-hover hover:text-danger"
          >
            <LogOut size={15} aria-hidden="true" />
            {S.auth.signOut}
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        {/* Not mounted while a platform-only caller is being sent away: every
            screen here would fire a query the BFF refuses. */}
        {platformOnly ? null : <Outlet />}
      </main>
    </div>
  );
}
